export function countryToFlagEmoji(country) {
  const code = String(country ?? '')
    .trim()
    .toUpperCase();
  if (code.length !== 2) return '🏳️';
  return String.fromCodePoint(...[...code].map((ch) => 127397 + ch.charCodeAt(0)));
}

export function normalizeSupportedFiat(item, idx = 0) {
  const country = String(item?.country ?? '').trim();
  const currency = String(item?.currency ?? '')
    .trim()
    .toUpperCase();
  const countryName = String(item?.countryName ?? '').trim();
  return {
    key: `${country || 'xx'}-${currency || 'xxx'}-${idx}`,
    country,
    countryName,
    currency,
    flagEmoji: countryToFlagEmoji(country),
    payMin: item.payMin,
    payMax: item.payMax,
    payWayCode: item.payWayCode,
    payWays: item.payWays,
  };
}

/** 新接口：扁平 fiats[]（每条 = 国家+法币+一种支付方式，自带 payMin/payMax） */
function flattenFlatFiats(fiats) {
  const map = new Map();

  fiats.forEach((item) => {
    const country = String(item?.country ?? '').trim();
    const countryName = String(item?.countryName ?? '').trim();
    const currency = String(item?.currency ?? '')
      .trim()
      .toUpperCase();
    if (!currency) return;

    const groupKey = `${country}-${currency}`;
    const payMin = Number(item?.payMin);
    const payMax = Number(item?.payMax);
    const safePayMin = Number.isFinite(payMin) && payMin > 0 ? payMin : undefined;
    const safePayMax = Number.isFinite(payMax) && payMax > 0 ? payMax : undefined;

    if (!map.has(groupKey)) {
      map.set(groupKey, {
        country,
        countryName,
        currency,
        payMin: safePayMin,
        payMax: safePayMax,
        payWays: [],
      });
    }

    const row = map.get(groupKey);
    if (countryName && !row.countryName) row.countryName = countryName;

    row.payWays.push({
      payWayCode: item?.payWayCode,
      payWayName: item?.payWayName,
      payMin: safePayMin,
      payMax: safePayMax,
      feeRate: item?.feeRate,
      fixedFee: item?.fixedFee,
    });

    if (safePayMin != null) {
      row.payMin =
        row.payMin != null && Number.isFinite(Number(row.payMin))
          ? Math.min(Number(row.payMin), safePayMin)
          : safePayMin;
    }
    if (safePayMax != null) {
      row.payMax =
        row.payMax != null && Number.isFinite(Number(row.payMax))
          ? Math.max(Number(row.payMax), safePayMax)
          : safePayMax;
    }
  });

  return [...map.values()];
}

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** 旧接口：countries → currencies → payWays（buy/sell 限额字段均兼容） */
function flattenCountriesTree(countries) {
  const rows = [];
  countries.forEach((countryItem) => {
    const country = String(countryItem?.country ?? '').trim();
    const countryName = String(countryItem?.countryName ?? '').trim();
    const currencies = Array.isArray(countryItem?.currencies) ? countryItem.currencies : [];

    currencies.forEach((currencyItem) => {
      const currency = String(currencyItem?.currency ?? '')
        .trim()
        .toUpperCase();
      if (!currency) return;

      const payMin = toPositiveNumber(
        currencyItem?.payMin ??
          currencyItem?.minPurchaseAmount ??
          currencyItem?.minSellAmount
      );
      const payMax = toPositiveNumber(
        currencyItem?.payMax ??
          currencyItem?.maxPurchaseAmount ??
          currencyItem?.maxSellAmount
      );
      const rawPayWays = Array.isArray(currencyItem?.payWays) ? currencyItem.payWays : [];
      const payWays = rawPayWays.map((pw) => {
        const wayMin = toPositiveNumber(pw?.payMin) ?? payMin;
        const wayMax = toPositiveNumber(pw?.payMax) ?? payMax;
        return {
          payWayCode: pw?.payWayCode,
          payWayName: pw?.payWayName,
          payMin: wayMin,
          payMax: wayMax,
          feeRate: pw?.feeRate,
          fixedFee: pw?.fixedFee,
        };
      });

      rows.push({
        country,
        countryName,
        currency,
        payMin,
        payMax,
        payWays,
      });
    });
  });
  return rows;
}

export function flattenSupportedFiats(payload) {
  const fiats = Array.isArray(payload?.fiats)
    ? payload.fiats
    : Array.isArray(payload?.data?.fiats)
      ? payload.data.fiats
      : null;

  if (fiats?.length) {
    return flattenFlatFiats(fiats);
  }

  const countries = Array.isArray(payload?.countries)
    ? payload.countries
    : Array.isArray(payload?.data?.countries)
      ? payload.data.countries
      : Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

  return flattenCountriesTree(countries);
}

export function normalizeSupportedFiatRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((item, idx) => normalizeSupportedFiat(item, idx));
}

/** Alchemy 返回的越南扫码；与 TopPay 通道重复时要从现金列表里拿掉。 */
export function isAlchemyVietQrPayWay(payWay) {
  const name = String(payWay?.payWayName ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  return name === 'VIET_QR' || name === 'VIETQR';
}

export function withoutAlchemyVietQrPayWays(payWays) {
  const list = Array.isArray(payWays) ? payWays : [];
  return list.filter((pw) => !isAlchemyVietQrPayWay(pw));
}
